/**
 * Grail header: <site-header> (sticky state + --header-height),
 * <header-drawer> (mobile menu: focus management, inert page, closes on
 * desktop resize, opens while the header is selected in the theme editor)
 * and <header-menu> (desktop dropdowns: one open at a time, hover intent for
 * mouse users, close when focus moves on).
 */
(() => {
  if (customElements.get('site-header')) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

  /** Makes everything outside `element` inert; returns a restore function. */
  function inertOutside(element) {
    const changed = [];
    let node = element;
    while (node && node.parentElement && node !== document.body) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || sibling.inert || ['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK'].includes(sibling.tagName)) continue;
        sibling.inert = true;
        changed.push(sibling);
      }
      node = node.parentElement;
    }
    return () => changed.forEach((el) => (el.inert = false));
  }

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.setHeight = () => document.documentElement.style.setProperty('--header-height', `${Math.round(this.getBoundingClientRect().height)}px`);
      this.resizeObserver = new ResizeObserver(this.setHeight);
      this.resizeObserver.observe(this);

      if (this.hasAttribute('data-sticky')) {
        this.onScroll = () => {
          const stuck = window.scrollY > 0 && this.getBoundingClientRect().top <= 0;
          this.toggleAttribute('data-scrolled', stuck);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }

  class HeaderDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details.querySelector('summary');
      this.panel = this.querySelector('[data-drawer-panel]');

      // Listeners on the drawer's own markup survive a move (theme editor reorder): add them once.
      if (!this.bound) {
        this.bound = true;
        this.onToggle = () => (this.details.open ? this.onOpen() : this.onClose());
        this.details.addEventListener('toggle', this.onToggle);

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-drawer-close]')) this.close(true);
        });
      }

      this.onMedia = (event) => {
        if (event.matches && this.details.open) this.close(false);
      };
      desktop.addEventListener('change', this.onMedia);

      // Theme editor: show the menu while the header section is selected (mobile widths only).
      if (window.Shopify?.designMode) {
        this.onEditorSelect = (event) => {
          if (event.target.contains(this) && !desktop.matches && !this.isOpen) this.details.open = true;
        };
        this.onEditorDeselect = (event) => {
          if (event.target.contains(this) && this.isOpen) this.close(false);
        };
        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
      }
    }

    disconnectedCallback() {
      desktop.removeEventListener('change', this.onMedia);
      if (this.onEditorSelect) document.removeEventListener('shopify:section:select', this.onEditorSelect);
      if (this.onEditorDeselect) document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
      this.restoreInert?.();
      this.restoreInert = null;
    }

    get isOpen() {
      return this.details.open;
    }

    onOpen() {
      this.restoreInert?.();
      this.restoreInert = inertOutside(this.details);
      requestAnimationFrame(() => this.panel.querySelector(FOCUSABLE)?.focus());
    }

    onClose() {
      this.restoreInert?.();
      this.restoreInert = null;
      this.panel.querySelectorAll('details[open]').forEach((group) => group.removeAttribute('open'));
    }

    close(returnFocus) {
      this.details.removeAttribute('open');
      // The toggle event is async: lift the inert page now, so whatever opens next (search) is usable.
      this.onClose();
      if (returnFocus) this.summary.focus();
    }
  }

  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('details[data-menu-item]')];
      if (this.bound) return; // moved in the DOM (theme editor): listeners are still attached
      this.bound = true;

      this.items.forEach((details) => {
        let timer;
        details.addEventListener('toggle', () => {
          if (details.open) this.items.forEach((other) => other !== details && other.removeAttribute('open'));
        });
        details.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(timer);
          if (!details.open) {
            details.open = true;
            details.dataset.hoverOpened = String(Date.now());
          }
        });
        details.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          timer = setTimeout(() => {
            if (!details.contains(document.activeElement) || document.activeElement === details.querySelector('summary')) {
              details.removeAttribute('open');
            }
          }, 200);
        });
        // Tabbing past the last link (or back before the summary) closes the dropdown.
        details.addEventListener('focusout', (event) => {
          if (details.open && event.relatedTarget && !details.contains(event.relatedTarget)) details.removeAttribute('open');
        });
        // A click right after hover-open should not immediately close the menu.
        details.querySelector('summary').addEventListener('click', (event) => {
          const openedAt = Number(details.dataset.hoverOpened || 0);
          if (details.open && Date.now() - openedAt < 600) event.preventDefault();
          delete details.dataset.hoverOpened;
        });
      });
    }
  }

  customElements.define('site-header', SiteHeader);
  customElements.define('header-drawer', HeaderDrawer);
  customElements.define('header-menu', HeaderMenu);
})();
